/* eslint-disable react-hooks/exhaustive-deps */
/**
 * /partner-hygiene — Hygiene Service Partners landing + registration form.
 *
 * Matches the uploaded reference exactly:
 *   • Hero: eyebrow + split headline + copy + two CTAs + three mini
 *     benefit chips, and (right side) a hero image carrying four
 *     workflow cards PLUS an in-hero registration form.
 *   • "Bring your hygiene services to more customers" — 6-card
 *     service opportunities grid with photos.
 *   • Platform benefits — 8 feature cards.
 *   • "How partnership works" — 5-step process on a pink wash.
 *   • Final CTA strip with Apply + Contact buttons.
 *
 * Form submits to POST /api/public/partner-enquiry with
 * category='hygiene'. Mobile-first.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Users, Calendar, ChartLine, Sparkle, Bathtub, Couch,
  Building, FirstAid, Drop, Laptop, CreditCard, Headset, FileText,
  CheckCircle, Handshake, Gear, Buildings, Phone, Envelope, MapPin,
  Clipboard, House, IdentificationCard, ClipboardText,
  CalendarCheck, CheckSquare, Camera,
} from "@phosphor-icons/react";
import { MarketingNav, MarketingFooter } from "./_shared";
import { usePageImages } from "../../lib/pageImages";
import { PartnerRegisterForm } from "./PartnerRegisterForm";
import SEO, {
  CITY_BRANCHES, organizationSchema, localBusinessSchema,
} from "../../lib/seo";

const OPPORTUNITIES = [
  { key: "home_deep_cleaning", title: "Home Deep Cleaning", Icon: House,
    body: "Comprehensive cleaning services for homes and apartments.",
    photo: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=70" },
  { key: "bathroom_kitchen", title: "Bathroom & Kitchen Cleaning", Icon: Bathtub,
    body: "Specialised cleaning for high-use household areas.",
    photo: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=800&q=70" },
  { key: "upholstery", title: "Sofa, Mattress & Upholstery Cleaning", Icon: Couch,
    body: "Professional fabric and upholstery cleaning services.",
    photo: "https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?auto=format&fit=crop&w=800&q=70" },
  { key: "office_commercial", title: "Office & Commercial Cleaning", Icon: Building,
    body: "Scheduled and contract-based cleaning for workplaces.",
    photo: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=70" },
  { key: "healthcare_facility", title: "Healthcare Facility Cleaning", Icon: FirstAid,
    body: "Cleaning and hygiene services for eligible clinics and hospitals.",
    photo: "https://images.unsplash.com/photo-1587351021355-a479a299d2f9?auto=format&fit=crop&w=800&q=70" },
  { key: "water_tank", title: "Water Tank & Specialised Cleaning", Icon: Drop,
    body: "Water tank, post-construction and specialised cleaning.",
    photo: "https://images.unsplash.com/photo-1536337005238-94b997371b40?auto=format&fit=crop&w=800&q=70" },
];

const BENEFITS = [
  { Icon: Users,     title: "Customer Booking Discovery",
    body: "Receive relevant enquiries from customers in your area." },
  { Icon: ClipboardText, title: "Service Catalogue Management",
    body: "Maintain service packages, pricing and durations." },
  { Icon: CalendarCheck, title: "Staff & Team Scheduling",
    body: "Assign teams and manage appointments." },
  { Icon: CheckSquare, title: "Service Checklists",
    body: "Use digital checklists for consistent service delivery." },
  { Icon: Camera,    title: "Before & After Documentation",
    body: "Record service photos with customer consent." },
  { Icon: CreditCard,title: "Payments & Settlements",
    body: "Access booking records and settlement information." },
  { Icon: Laptop,    title: "Partner Dashboard",
    body: "Manage bookings, teams and operational reports." },
  { Icon: Headset,   title: "Partner Support",
    body: "Get onboarding guidance and account assistance." },
];

const PROCESS = [
  { n: "01", Icon: IdentificationCard, title: "Register Your Business",
    body: "Submit company details and service categories." },
  { n: "02", Icon: CheckSquare, title: "Complete Verification",
    body: "Provide required documents, staff information and safety procedures." },
  { n: "03", Icon: Gear, title: "Configure Your Services",
    body: "Set locations, packages, team availability and operating hours." },
  { n: "04", Icon: FileText, title: "Review Partnership Terms",
    body: "Understand fees, responsibilities and service standards." },
  { n: "05", Icon: CheckCircle, title: "Start Receiving Enquiries",
    body: "Following approval, manage bookings through your partner dashboard." },
];

function OpportunityCard({ o }) {
  const Icon = o.Icon;
  return (
    <article data-testid={`hyg-opp-${o.title.replace(/\s+/g,'-').toLowerCase()}`}
             className="rounded-2xl overflow-hidden bg-white border border-[#F0EEEA]
                         shadow-sm hover:shadow-md transition-shadow">
      <div className="relative aspect-[16/10] bg-[#F7F6F2]">
        <img src={o.photo} alt="" loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"/>
      </div>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#FDE2E4] text-[#D23247]
                           grid place-items-center shrink-0">
            <Icon size={18} weight="duotone"/>
          </div>
          <div className="min-w-0">
            <h3 className="font-heading text-base text-slate-900 leading-tight">{o.title}</h3>
            <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">{o.body}</p>
          </div>
        </div>
        <a href="#partner-contact"
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#D23247]">
          Explore Opportunity <ArrowRight size={11} weight="bold"/>
        </a>
      </div>
    </article>
  );
}

function BenefitCard({ b }) {
  const Icon = b.Icon;
  return (
    <div className="p-4 rounded-xl bg-white border border-[#F0EEEA] flex gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#FDE2E4] text-[#D23247]
                       grid place-items-center shrink-0">
        <Icon size={18} weight="duotone"/>
      </div>
      <div className="min-w-0">
        <div className="font-heading text-[14px] text-slate-900 leading-tight">{b.title}</div>
        <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">{b.body}</p>
      </div>
    </div>
  );
}

const RegisterForm = ({ compact }) => (
  <PartnerRegisterForm compact={compact} category="hygiene" tag="Hygiene partner registration"
    testPrefix="hyg" title="Register Your Hygiene Company"
    subtitle="Join the Rescigo Health partner network."
    defaultMessage="I'd like to register as a hygiene service partner."
    successMsg="Thanks! Our hygiene partnerships team will reach out."/>
);

export default function PartnerHygieneLanding() {
  const images = usePageImages("partner-hygiene");
  const jsonLd = [organizationSchema(), ...CITY_BRANCHES.map(localBusinessSchema)];
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Partner with us", path: "/partner" },
    { name: "Hygiene Service Partners", path: "/partner-hygiene" },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="page-partner-hygiene">
      <SEO
        title="Hygiene Service Partners — grow your cleaning business with Rescigo Health"
        description="Register your professional cleaning or hygiene company with the Rescigo Health partner network. Reach more customers, manage teams, and grow your business across Kerala."
        path="/partner-hygiene"
        keywords="hygiene partner Kerala, cleaning service partner, deep cleaning partner, sanitation partner Kerala"
        jsonLd={jsonLd}
        breadcrumbs={breadcrumbs}
      />
      <MarketingNav/>

      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="pt-24 sm:pt-28 pb-10 sm:pb-14 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
              — Hygiene Service Partners
            </div>
            <h1 className="font-heading text-[36px] sm:text-5xl lg:text-[56px] leading-[1.08] mt-5 text-slate-900">
              More bookings.<br/>
              <span className="text-[#D23247]">Better service<br/>management.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-lg leading-relaxed">
              Connect your professional cleaning and hygiene business with
              customers seeking reliable home, commercial and institutional
              hygiene services through the Rescigo Health partner network.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a href="#hyg-register"
                  data-testid="hyg-hero-register"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full
                              bg-[#D23247] hover:bg-[#B52236] text-white
                              text-sm font-semibold transition-colors">
                Register Your Hygiene Company <ArrowRight size={14} weight="bold"/>
              </a>
              <a href="#hyg-benefits"
                  data-testid="hyg-hero-explore"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full
                              border border-[#D23247] text-[#D23247] hover:bg-[#FDE2E4]
                              text-sm font-semibold transition-colors">
                Explore Partner Platform <ArrowRight size={14} weight="bold"/>
              </a>
            </div>
            {/* Three mini chips (as in reference) */}
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { Icon: Users,     title: "Reach more customers",
                  body: "Get service enquiries in your operating area." },
                { Icon: Calendar,  title: "Manage your teams",
                  body: "Bookings, schedules and service tracking." },
                { Icon: ChartLine, title: "Grow your business",
                  body: "A connected platform for long-term opportunities." },
              ].map((c, i) => {
                const Ico = c.Icon;
                return (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-[#FDE2E4] text-[#D23247]
                                     grid place-items-center shrink-0">
                      <Ico size={16} weight="duotone"/>
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold text-slate-900 leading-tight">{c.title}</div>
                      <p className="text-[11px] text-slate-600 mt-1 leading-snug">{c.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: hero image + workflow cards + registration form */}
          <div className="relative">
            <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] bg-[#FDE2E4]">
              <img
                src={images["hero.main"] || "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=70"}
                alt="Hygiene team cleaning a modern home"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* Workflow floating cards (hidden on narrow) */}
            {[
              { Icon: CalendarCheck, label: "New Cleaning Booking",   sub: "Home Deep Cleaning",   top: "top-6" },
              { Icon: Users,         label: "Team Assigned",          sub: "3 members · 10:00 AM", top: "top-24" },
              { Icon: ClipboardText, label: "Service Checklist",      sub: "In progress",          top: "top-44" },
              { Icon: CheckCircle,   label: "Job Completed",          sub: "Photos and feedback",  top: "top-[256px]" },
            ].map((c, i) => {
              const Ico = c.Icon;
              return (
                <div key={i}
                      className={`hidden md:flex absolute ${c.top} left-5
                                   bg-white rounded-xl shadow-xl border border-[#F0EEEA]
                                   p-2.5 pr-4 items-center gap-2.5 max-w-[220px]`}>
                  <div className="w-9 h-9 rounded-lg bg-[#FDE2E4] text-[#D23247]
                                   grid place-items-center shrink-0">
                    <Ico size={18} weight="duotone"/>
                  </div>
                  <div>
                    <div className="text-[12px] font-semibold text-slate-900 leading-tight">{c.label}</div>
                    <div className="text-[10px] text-slate-500 leading-tight">{c.sub}</div>
                  </div>
                </div>
              );
            })}

            {/* Floating register form (desktop) */}
            <div id="hyg-register"
                  className="hidden lg:block absolute top-6 right-0 w-[340px] xl:w-[380px]">
              <RegisterForm/>
            </div>
          </div>
        </div>

        {/* Mobile / tablet register form (shown when desktop floating is hidden) */}
        <div className="lg:hidden mt-8" id="hyg-register-mobile">
          <RegisterForm compact/>
        </div>
      </section>

      {/* ============================================================
          OPPORTUNITIES
          ============================================================ */}
      <section className="pb-14 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
          — Service Opportunities
        </div>
        <div className="flex items-end justify-between mt-3 gap-4 flex-wrap">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl text-slate-900">
              Bring your hygiene services <span className="text-[#D23247]">to more customers.</span>
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              List your services, receive bookings and manage your operations through a connected partner platform.
            </p>
          </div>
          <a href="#hyg-benefits"
              className="text-sm font-semibold text-[#D23247] inline-flex items-center gap-1">
            Explore all hygiene service categories <ArrowRight size={12} weight="bold"/>
          </a>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {OPPORTUNITIES.map(o => <OpportunityCard key={o.title}
              o={{ ...o, photo: images[`opportunities.${o.key}`] || o.photo }}/>)}
        </div>
      </section>

      {/* ============================================================
          BENEFITS
          ============================================================ */}
      <section id="hyg-benefits" className="pb-14 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
          — Platform Benefits
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 mt-3">
          Your cleaning expertise. <span className="text-[#D23247]">Our connected platform.</span>
        </h2>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Tools, workflows and support to help your hygiene service business operate efficiently.
        </p>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {BENEFITS.map((b, i) => <BenefitCard key={i} b={b}/>)}
        </div>
      </section>

      {/* ============================================================
          PROCESS
          ============================================================ */}
      <section className="pb-16 bg-[#FDE2E4]/40">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
            — How Partnership Works
          </div>
          <div className="flex items-end justify-between flex-wrap gap-3 mt-3">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl text-slate-900">
                A simple and transparent process.
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                From registration to active bookings, we make it easy to get started.
              </p>
            </div>
          </div>
          <ol className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
            {PROCESS.map((p, i) => {
              const Ico = p.Icon;
              return (
                <li key={p.n} className="rounded-2xl bg-white border border-[#F0EEEA] p-4 relative">
                  <div className="text-[11px] font-mono text-[#D23247]">{p.n}</div>
                  <div className="w-9 h-9 rounded-xl bg-[#FDE2E4] text-[#D23247]
                                   grid place-items-center mt-2">
                    <Ico size={18} weight="duotone"/>
                  </div>
                  <div className="font-heading text-[14px] text-slate-900 mt-3 leading-tight">{p.title}</div>
                  <p className="text-[11px] text-slate-600 mt-1 leading-snug">{p.body}</p>
                  {i < PROCESS.length - 1 && (
                    <ArrowRight size={14} weight="bold"
                                 className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-[#D23247]"/>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA STRIP
          ============================================================ */}
      <section id="partner-contact" className="py-14 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="rounded-3xl bg-[#FDE2E4]/50 border border-[#F0EEEA] p-6 sm:p-8
                         flex flex-wrap items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-white text-[#D23247] grid place-items-center shrink-0">
            <Handshake size={26} weight="duotone"/>
          </div>
          <div className="flex-1 min-w-[260px]">
            <h3 className="font-heading text-xl sm:text-2xl text-slate-900">
              Join a growing network of hygiene service providers.
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Together, we can make clean and healthy spaces more accessible for homes, businesses and healthcare facilities.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="#hyg-register"
                data-testid="hyg-final-apply"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full
                            bg-[#D23247] hover:bg-[#B52236] text-white text-sm font-semibold">
              Apply as a Hygiene Partner <ArrowRight size={14} weight="bold"/>
            </a>
            <Link to="/partner"
                   data-testid="hyg-final-contact"
                   className="inline-flex items-center gap-2 h-11 px-5 rounded-full
                               border border-[#D23247] text-[#D23247] hover:bg-[#FDE2E4]
                               text-sm font-semibold">
              Contact Partnerships Team <ArrowRight size={14} weight="bold"/>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}
